import { Suspense, lazy, useEffect, useState, type ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./lib/supabase";
import { useIsAdmin } from "./lib/useIsAdmin";
import { AppShell } from "./components/AppShell";
import { Help } from "./pages/Help";
import { Login } from "./pages/Login";
import { MeetingsList } from "./pages/MeetingsList";
import { MeetingNew } from "./pages/MeetingNew";
import { Projects } from "./pages/Projects";

// Route-level code splitting. The review workbench (and wavesurfer.js under
// it), the project detail page, and the admin pages load only when their
// route is visited — the landing library page ships none of them. Vite
// splits each dynamic import into its own chunk automatically.
const MeetingReview = lazy(() =>
  import("./pages/MeetingReview").then((m) => ({ default: m.MeetingReview })),
);
const ProjectDetail = lazy(() =>
  import("./pages/ProjectDetail").then((m) => ({ default: m.ProjectDetail })),
);
const AdminOverview = lazy(() =>
  import("./pages/admin/AdminOverview").then((m) => ({ default: m.AdminOverview })),
);
const AdminUsers = lazy(() =>
  import("./pages/admin/AdminUsers").then((m) => ({ default: m.AdminUsers })),
);
const AdminAudit = lazy(() =>
  import("./pages/admin/AdminAudit").then((m) => ({ default: m.AdminAudit })),
);

// Guard component: redirects to "/" when admin check resolves to non-admin.
// Renders nothing while the check is in flight to avoid a flash.
function AdminGuard({ children }: { children: ReactNode }) {
  const { isAdmin, loading } = useIsAdmin();
  if (loading) return null;
  if (!isAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function App() {
  const [session, setSession] = useState<Session | null | undefined>(undefined);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  if (session === undefined) {
    // Initial session probe in flight — show nothing rather than flash login.
    return null;
  }

  if (!session) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <AppShell session={session}>
      {/* fallback null matches the AdminGuard/loading conventions: the shell
          stays put and the page area is blank for the split-second chunk
          fetch, rather than flashing a spinner. */}
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<MeetingsList />} />
          <Route path="/meetings/new" element={<MeetingNew />} />
          <Route path="/meetings/:id" element={<MeetingReview />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/projects/:id" element={<ProjectDetail />} />
          <Route path="/help" element={<Help />} />
          <Route
            path="/admin"
            element={
              <AdminGuard>
                <AdminOverview />
              </AdminGuard>
            }
          />
          <Route
            path="/admin/users"
            element={
              <AdminGuard>
                <AdminUsers />
              </AdminGuard>
            }
          />
          <Route
            path="/admin/audit"
            element={
              <AdminGuard>
                <AdminAudit />
              </AdminGuard>
            }
          />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </AppShell>
  );
}
